import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { AppFooter } from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api, formatApiError, setAuthToken } from "@/lib/api";
import { toast } from "sonner";
import { Mail, KeyRound, ArrowLeft, HelpCircle } from "lucide-react";

// Steps: "number" → "password" (if has_password) OR "otp" → "create-password" (if first-time)
export default function Login() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { setUser } = useAuth();
  const [step, setStep] = useState("number");
  const [patientNumber, setPatientNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [code, setCode] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [forgotNumberOpen, setForgotNumberOpen] = useState(false);
  const [forgotPwdOpen, setForgotPwdOpen] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const checkNumber = async (e) => {
    e.preventDefault();
    if (!patientNumber.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/patient/check", { patient_number: patientNumber.trim() });
      setEmailHint(data.email_hint || "");
      if (data.has_password) {
        setStep("password");
      } else {
        // first-time: request OTP
        await api.post("/auth/patient/request-otp", { patient_number: patientNumber.trim() });
        setResendIn(60);
        setStep("otp");
        toast.success("Verification code sent");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/patient/login", {
        patient_number: patientNumber.trim(),
        password,
      });
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/me/today", { replace: true });
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/patient/verify-otp", {
        patient_number: patientNumber.trim(),
        code: code.trim(),
      });
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data.user);
      if (data.requires_password_setup) {
        setStep("create-password");
      } else {
        toast.success(`Welcome, ${data.user.name}`);
        navigate("/me/today", { replace: true });
      }
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const resendOtp = async () => {
    if (resendIn > 0 || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/patient/request-otp", { patient_number: patientNumber.trim() });
      setResendIn(60);
      toast.success("Code resent");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const createPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirmPwd) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/patient/set-password", { password });
      toast.success("Password created — confirmation sent to your email");
      navigate("/me/today", { replace: true });
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const reset = () => {
    setStep("number"); setCode(""); setPassword(""); setConfirmPwd(""); setEmailHint("");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="login-page">
      <main className="flex-1 grid lg:grid-cols-2">
        <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
          <div className="max-w-md w-full mx-auto">
            <Logo size="lg" />
            <div className="mt-12">
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground tracking-tight">{settings.login_title}</h1>
              <p className="mt-3 text-muted-foreground">{settings.login_subtitle}</p>
            </div>

            {step === "number" && (
              <form onSubmit={checkNumber} className="space-y-5 mt-10" data-testid="patient-login-form">
                <div className="space-y-2">
                  <Label htmlFor="patient-number">Patient number</Label>
                  <Input id="patient-number" required autoFocus value={patientNumber} onChange={(e) => setPatientNumber(e.target.value)} placeholder="e.g. 0000608000" className="h-12 font-mono tracking-wider text-lg" data-testid="patient-number-input" />
                </div>
                <Button type="submit" disabled={submitting || !patientNumber.trim()} className="w-full h-12 rounded-full font-semibold" data-testid="continue-button">
                  {submitting ? "Please wait…" : "Continue"}
                </Button>
                <div className="flex items-center justify-between text-sm pt-2">
                  <button type="button" onClick={() => setForgotNumberOpen(true)} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="forgot-number-link">
                    <HelpCircle size={14} /> Forgot your patient number?
                  </button>
                  <button type="button" onClick={() => setForgotPwdOpen(true)} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
              </form>
            )}

            {step === "password" && (
              <form onSubmit={passwordLogin} className="space-y-5 mt-10" data-testid="patient-password-form">
                <div className="rounded-lg bg-secondary text-secondary-foreground px-4 py-3 text-sm">
                  <div className="font-semibold">Patient <span className="font-mono">{patientNumber}</span></div>
                  <div className="text-xs">Enter your password to sign in.</div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <PasswordInput id="password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" testId="password-input" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full h-12 rounded-full font-semibold" data-testid="password-submit-button">
                  {submitting ? "Signing in…" : "Sign in"}
                </Button>
                <div className="flex items-center justify-between text-sm pt-2">
                  <button type="button" onClick={reset} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-button">
                    <ArrowLeft size={14} /> Change patient number
                  </button>
                  <button type="button" onClick={() => setForgotPwdOpen(true)} className="text-primary font-medium" data-testid="forgot-password-link-2">
                    Forgot password?
                  </button>
                </div>
              </form>
            )}

            {step === "otp" && (
              <form onSubmit={verifyOtp} className="space-y-5 mt-10" data-testid="otp-form">
                <div className="rounded-lg bg-secondary text-secondary-foreground px-4 py-3 text-sm flex items-start gap-3">
                  <Mail size={18} className="mt-0.5" />
                  <div>
                    <div className="font-semibold">Code sent</div>
                    <div className="text-xs">A 6-digit code was sent to {emailHint || "your registered email"}. Valid for 10 minutes.</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp">Verification code</Label>
                  <Input id="otp" required autoFocus inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" className="h-14 text-center text-2xl font-mono tracking-[0.6em]" data-testid="otp-input" />
                </div>
                <Button type="submit" disabled={submitting || code.length !== 6} className="w-full h-12 rounded-full font-semibold" data-testid="verify-otp-button">
                  {submitting ? "Verifying…" : "Verify & continue"}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={reset} className="text-muted-foreground hover:text-foreground" data-testid="change-number-button">
                    ← Change patient number
                  </button>
                  <button type="button" disabled={resendIn > 0 || submitting} onClick={resendOtp} className="text-primary font-medium disabled:text-muted-foreground" data-testid="resend-otp-button">
                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                  </button>
                </div>
              </form>
            )}

            {step === "create-password" && (
              <form onSubmit={createPassword} className="space-y-5 mt-10" data-testid="create-password-form">
                <div className="rounded-lg bg-secondary text-secondary-foreground px-4 py-3 text-sm flex items-start gap-3">
                  <KeyRound size={18} className="mt-0.5" />
                  <div>
                    <div className="font-semibold">Create your password</div>
                    <div className="text-xs">You'll use this to sign in next time. A confirmation will be sent to {emailHint}.</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-pwd">New password</Label>
                  <PasswordInput id="new-pwd" required minLength={6} autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" testId="new-password-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-pwd">Confirm password</Label>
                  <PasswordInput id="confirm-pwd" required value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="h-12" testId="confirm-password-input" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full h-12 rounded-full font-semibold" data-testid="create-password-button">
                  {submitting ? "Saving…" : "Create password & continue"}
                </Button>
              </form>
            )}
          </div>
        </div>

        <div className="hidden lg:block relative overflow-hidden">
          <img src="https://images.pexels.com/photos/7991909/pexels-photo-7991909.jpeg" alt="Healthy nutrition" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-transparent" />
          <div className="absolute bottom-12 left-12 right-12">
            <p className="text-3xl font-bold leading-tight max-w-md text-white">{settings.hero_title}</p>
            <p className="mt-3 text-white/85 max-w-md">{settings.hero_subtitle}</p>
          </div>
        </div>
      </main>
      <AppFooter />

      <ForgotNumberDialog open={forgotNumberOpen} onOpenChange={setForgotNumberOpen} />
      <ForgotPasswordDialog open={forgotPwdOpen} onOpenChange={setForgotPwdOpen} initialNumber={patientNumber} />
    </div>
  );
}

function ForgotNumberDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/patient/forgot-number", { email });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) { setEmail(""); setSent(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="forgot-number-dialog">
        <DialogHeader><DialogTitle>Forgot your patient number?</DialogTitle></DialogHeader>
        {!sent ? (
          <form onSubmit={submit} className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Enter your registered email. Your patient number will be sent to you shortly.</p>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-number-email-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full" data-testid="forgot-number-submit">
              {submitting ? "Sending…" : "Send my patient number"}
            </Button>
          </form>
        ) : (
          <div className="py-6 text-center space-y-3" data-testid="forgot-number-sent">
            <div className="mx-auto w-12 h-12 rounded-full bg-secondary flex items-center justify-center"><Mail size={22} className="text-primary" /></div>
            <p className="font-semibold">Check your email</p>
            <p className="text-sm text-muted-foreground">If an account exists for that email, we've sent the patient number.</p>
            <Button variant="outline" onClick={() => close(false)} className="mt-3">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ForgotPasswordDialog({ open, onOpenChange, initialNumber = "" }) {
  const [step, setStep] = useState("number"); // number -> email
  const [pnum, setPnum] = useState(initialNumber);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { if (open) setPnum(initialNumber); }, [open, initialNumber]);

  const submitNumber = (e) => { e.preventDefault(); if (pnum.trim()) setStep("email"); };

  const submitEmail = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/patient/forgot-password", { patient_number: pnum.trim(), email });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) { setStep("number"); setPnum(initialNumber); setEmail(""); setSent(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader><DialogTitle>Reset your password</DialogTitle></DialogHeader>
        {sent ? (
          <div className="py-6 text-center space-y-3" data-testid="forgot-password-sent">
            <div className="mx-auto w-12 h-12 rounded-full bg-secondary flex items-center justify-center"><Mail size={22} className="text-primary" /></div>
            <p className="font-semibold">Check your email</p>
            <p className="text-sm text-muted-foreground">If the details match, we've sent a reset link to your email. The link is valid for 1 hour.</p>
            <Button variant="outline" onClick={() => close(false)} className="mt-3">Close</Button>
          </div>
        ) : step === "number" ? (
          <form onSubmit={submitNumber} className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Step 1 — Enter your patient number.</p>
            <div className="space-y-2">
              <Label>Patient number</Label>
              <Input required autoFocus value={pnum} onChange={(e) => setPnum(e.target.value)} className="font-mono" data-testid="forgot-password-number-input" />
            </div>
            <Button type="submit" disabled={!pnum.trim()} className="w-full" data-testid="forgot-password-next">Next</Button>
          </form>
        ) : (
          <form onSubmit={submitEmail} className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Step 2 — Confirm the email linked to patient <span className="font-mono font-semibold">{pnum}</span>.</p>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input required type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email-input" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("number")} className="flex-1">Back</Button>
              <Button type="submit" disabled={submitting} className="flex-1" data-testid="forgot-password-submit">
                {submitting ? "Sending…" : "Send reset link"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
